// src/service/Acceso.js
import instance from '../apis/app';
import { v2Get, v2Post, v2Put } from './client.js';

const Acceso = {
  /**
   * Genera un QR de acceso al recinto y habilita a la persona en el torniquete.
   * - Cliente: requiere inscripción confirmada vigente y clase hoy, desde 30 min antes
   *   de que empiece. Fuera de eso responde 403 y no habilita nada.
   *   El acceso queda vigente hasta el fin del día (la salida no se corta al terminar
   *   la clase), así que también puede entrar y salir con su cédula.
   * - Staff (cualquier rol con «Acceso al torniquete» marcado en Roles y permisos,
   *   `funcionario` incluido): acceso permanente, sin validación de inscripción ni de
   *   horario. No necesita la app para entrar.
   *
   * El acceso es del INSCRITO, no del titular de la cuenta: si el taller es de un
   * familiar, el QR lleva el RUT del familiar y abre para él. `inscripcionId` elige
   * de quién es cuando hay varios inscritos con bloque en curso a la misma hora.
   *
   * @param {string|null} inscripcionId
   * @returns {Promise<{ qrDataUrl: string, pin: string, expiraEn: string,
   *   tipoVentana: 'bloque' | 'permanente', rolUsado: 'cliente' | 'staff',
   *   contextoTaller: object | null, titular: { nombre: string, rut: string },
   *   otrosInscritos: Array<{ inscripcionId, nombre, taller, bloque }> }>}
   */
  GenerarQR: async (inscripcionId = null) => {
    try {
      // El interceptor de libs/axios.js ya devuelve res.data, así que aquí
      // `response` es el body del backend: { success: true, data: {...} }.
      const response = await instance.post('/acceso/generar-qr',
        inscripcionId ? { inscripcionId } : {});
      return response?.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'No se pudo generar el QR de acceso';
      throw { message, status: error.response?.status };
    }
  },
};

/**
 * Panel de Operaciones · Torniquete (admin / coordinador / jefe de recinto).
 *
 * Responde las dos preguntas que hoy llegan por WhatsApp —"¿está conectado?" y "¿por
 * qué no le abrió a esta persona?"— y resuelve la excepción de mostrador. Las tres
 * acciones que escriben en el equipo quedan auditadas en el backend con el sub del
 * token, así que acá no hace falta mandar quién las pidió.
 */
export const AccesoAdmin = {
  /** Estado de las puertas y hora del último evento de cada equipo. */
  equipos: async () => {
    const r = await v2Get('/admin/acceso/equipos');
    return r.data || r;
  },

  /** Diagnóstico completo de una persona: ficha, inscripciones, eventos y veredicto. */
  persona: async (rut) => {
    const r = await v2Get(`/admin/acceso/persona/${encodeURIComponent(rut)}`);
    return r.data || r;
  },

  eventos: async ({ desde = null, hasta = null, rut = null, pageNo = 1, pageSize = 50 } = {}) => {
    const r = await v2Get('/admin/acceso/eventos', { params: { desde, hasta, rut, pageNo, pageSize } });
    return r.data || r;
  },

  /** Último resumen del job diario. No lo recalcula: recorrer el equipo son ~2 min. */
  reconciliacion: async () => {
    const r = await v2Get('/admin/acceso/reconciliacion');
    return r.data || r;
  },

  habilitar: async ({ rut, minutos }) => {
    const r = await v2Post('/admin/acceso/habilitar', { rut, minutos });
    return r.data || r;
  },

  baja: async ({ rut }) => {
    const r = await v2Post('/admin/acceso/baja', { rut });
    return r.data || r;
  },

  /** Abre una puerta física a distancia. Confirmar SIEMPRE antes de llamar. */
  abrir: async ({ puerta, segundos }) => {
    const r = await v2Post('/admin/acceso/abrir', { puerta, segundos });
    return r.data || r;
  },

  /** Puertas del equipo con su etiqueta legible y su dirección. */
  puertas: async () => {
    const r = await v2Get('/admin/acceso/puertas');
    return r.data || r;
  },

  /**
   * Etiqueta una puerta. `direccion` ('entrada' | 'salida' | 'ambas') no es cosmética:
   * es lo que permite calcular la hora de salida en la asistencia, porque el equipo no
   * distingue entrada de salida por sí mismo.
   */
  guardarPuerta: async ({ doorName, devSn, etiqueta, direccion, notas }) => {
    const r = await v2Put('/admin/acceso/puertas', { doorName, devSn, etiqueta, direccion, notas });
    return r.data || r;
  },

  /**
   * Mismo RUT dos veces en una puerta de entrada en menos de 2 minutos (por defecto,
   * hoy). Puede ser alguien que no alcanzó a girar o dos personas con la misma
   * cédula/QR: el equipo no sabe quién gira. `otroTorniquete` es la señal fuerte.
   */
  doblesEntradas: async ({ desde = null, hasta = null } = {}) => {
    const r = await v2Get('/admin/acceso/dobles-entradas', { params: { desde, hasta } });
    return r.data || r;
  },

  /** Quién pasó por el torniquete en el rango, con su taller y sus horas. */
  asistencia: async ({ desde, hasta }) => {
    const r = await v2Get('/admin/acceso/asistencia', { params: { desde, hasta } });
    return r.data || r;
  },
};

export default Acceso;
